import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/** Every drawer currently mounted, in the order they opened.
 *
 *  ESCAPE MUST CLOSE THE TOP ONE ONLY. These are designed to stack — the receipt
 *  drawer opens on top of the competitor dossier — and a plain window listener per
 *  drawer means one Escape fires both handlers and collapses the whole pile. You press
 *  Escape to back out of the receipt and lose the dossier you were reading it from. */
const openDrawers: symbol[] = [];

/** Escape closes this layer only when it is the top one - the same stack the
 *  drawers use, for a panel that is not a drawer (the board's slide-up panel).
 *  A drawer opened over it is pushed later, so it closes first. */
export function useEscapeTop(onClose: () => void, active = true) {
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) idRef.current = Symbol('layer');
  useEffect(() => {
    if (!active) return;
    const id = idRef.current as symbol;
    openDrawers.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (openDrawers[openDrawers.length - 1] !== id) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const at = openDrawers.indexOf(id);
      if (at >= 0) openDrawers.splice(at, 1);
    };
  }, [onClose, active]);
}

/**
 * THE RIGHT-HAND DRAWER — scrim, panel, sticky header, close.
 *
 * One shell, because the drawers are designed to STACK: the receipt drawer opens on
 * top of the competitor dossier, so any divergence in scrim, dark-mode surface or
 * scroll behaviour is visible as two slightly different panels sitting on each other.
 * They were copies differing only in width and z-index, which is exactly the pair of
 * values that has to differ.
 *
 * Clicking the scrim closes; clicking the panel does not.
 */
export default function Drawer({
  onClose,
  width = 'w-[34rem]',
  z = 'z-40',
  side = 'right',
  fill = false,
  eyebrow,
  title,
  actions,
  children,
}: {
  onClose: () => void;
  /** Tailwind width class for the panel. */
  width?: string;
  /** Tailwind z-index class. A drawer opened FROM another must sit above it. */
  z?: string;
  /** Which edge it slides from. Right for inspection — that is where every dossier and
   *  receipt lives, and they stack. Left is for NAVIGATION on a narrow screen, where the
   *  agenda rail cannot have a column of its own: coming from the side the rail normally
   *  occupies is the difference between "the same thing, temporarily overlaid" and "a
   *  second, unfamiliar panel". */
  side?: 'left' | 'right';
  /** Hand the body the panel's remaining height instead of letting it grow.
   *
   *  For content that scrolls its OWN regions. The default panel scrolls as a whole,
   *  so a child asking for `h-full` gets nothing to resolve against (the wrapper's
   *  height is auto) and grows to its content instead: a two-pane workspace ends up
   *  as tall as its longest column, and anything the other column pins to the bottom
   *  — a chat composer, a footer toolbar — sits a full list below the fold. With this
   *  on, the panel is a flex column, the body is a bounded box, and the child's own
   *  `overflow-auto` regions do the scrolling.
   *
   *  It also drops the body padding: content that draws its own headers, dividers and
   *  column borders wants to meet the panel edge. */
  fill?: boolean;
  /** The small uppercase label above the title — what kind of thing this is. */
  eyebrow: string;
  /** The header's own content, under the eyebrow. */
  title: ReactNode;
  /** Extra header controls, beside the × — a pin, say. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) idRef.current = Symbol('drawer');

  useEffect(() => {
    const id = idRef.current as symbol;
    openDrawers.push(id);
    return () => {
      const at = openDrawers.indexOf(id);
      if (at >= 0) openDrawers.splice(at, 1);
    };
  }, []);

  // THE PAGE BEHIND DOES NOT MOVE while a drawer is open.
  //
  // Two things go wrong without this, and the second is the one that looked like a
  // rendering bug. The obvious one: scrolling over the scrim scrolls the board
  // underneath, so you close the drawer and find yourself somewhere else. The other: the
  // panel widened the document, so the whole page became horizontally scrollable and
  // everything to the right of the drawer sat off-screen — which reads exactly like the
  // drawer being clipped, and is not.
  //
  // Save-and-restore rather than a counter, which makes STACKING fall out for free: the
  // receipt opens over the dossier and saves "hidden", so closing it restores "hidden"
  // and only the last drawer out puts the page back.
  // BOTH ELEMENTS. Setting `overflow` on `body` alone is supposed to propagate to the
  // viewport, but only while `html` leaves its own overflow as `visible` — and one
  // stylesheet touching `html` anywhere in the app silently breaks that, which is what
  // happened here: body was locked and the page went on scrolling. Locking the element
  // that actually scrolls is not worth being clever about.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPadding: body.style.paddingRight,
    };
    // Compensate for the scrollbar we are about to remove, or the whole page jumps
    // sideways by its width the moment a drawer opens.
    const gutter = window.innerWidth - html.clientWidth;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    if (gutter > 0) {
      const current = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${current + gutter}px`;
    }
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.paddingRight = prev.bodyPadding;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Topmost only. Anything below stays where it is.
      if (openDrawers[openDrawers.length - 1] !== idRef.current) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // RENDERED IN PLACE, for now.
  //
  // This was briefly portalled to `document.body`, on the theory that an ancestor had
  // made itself the containing block for `position: fixed` — `transform`, `filter`,
  // `backdrop-filter` and `contain` all do that, and the brainstorm card has candidates
  // (the run bar is `backdrop-blur`, the card is `overflow-clip`). That would explain a
  // panel pinned inside a region narrower than the screen with its prose clipped.
  //
  // It also stopped the drawers appearing at all, so it is backed out until the real
  // cause is known rather than guessed at. If the containing-block theory turns out to
  // be right, a portal IS the fix — but it needs to land with the reason understood.
  return (
    // max-w-[100vw] AND overflow-hidden, belt and braces against the thing that made this
    // look like a clipping bug.
    //
    // `inset-0` is only the viewport while nothing above has made itself the containing
    // block for fixed positioning — `transform`, `filter`, `backdrop-filter` and
    // `contain` all do, and this page has candidates. When one of them claims it, this
    // element sizes to THAT instead, contributes to the document's scrollable overflow,
    // and the page grows sideways: the drawer looks chopped, when really the rest of the
    // page is off to the right waiting to be scrolled to.
    //
    // Viewport units do not care which ancestor won. Capping here caps the panel too,
    // since its `max-w-full` resolves against this.
    <div
      className={`fixed inset-0 max-w-[100vw] overflow-hidden bg-black/30 ${z} flex ${
        side === 'left' ? 'justify-start' : 'justify-end'
      }`}
      onClick={onClose}
    >
      <div
        className={`${width} max-w-full bg-white dark:bg-gray-800 h-full shadow-xl ${
          fill ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'
        }`}
        onClick={e => e.stopPropagation()}
      >
        <div
          className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-start justify-between gap-4 ${
            // Sticky is how the header stays put when the PANEL scrolls; when the body
            // owns the scrolling the header is simply the flex row above it, and sticky
            // would only give it a scroll container it never moves in.
            fill ? 'shrink-0' : 'sticky top-0'
          }`}
        >
          <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-400 uppercase">
              {eyebrow}
            </div>
            {title}
          </div>
          <div className="flex items-start gap-2 shrink-0">
            {actions}
            <button
              onClick={onClose}
              title="Close"
              className="icon-btn text-gray-400 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 shrink-0"
            >
              ×
            </button>
          </div>
        </div>
        {/* PADDED HERE, not by each caller. The header already carried `p-4` and the body
            carried nothing, so every drawer had to remember its own wrapper — and the
            three added most recently did not, which is what a flush-left panel looks
            like. It is the same argument this component's own docstring makes about the
            scrim and the dark-mode surface: these things STACK, so a divergence shows up
            as two slightly different panels sitting on each other.

            Vertical rhythm stays the caller's business (`space-y-*`): how far apart the
            sections of a receipt should sit is not something the shell can know.

            `fill` is the one exception: content that draws its own headers and column
            borders is meant to meet the panel edge, and padding there would leave a
            white gutter around a full-height workspace. */}
        {/* break-words as well as padding. The padding is symmetric — it always was —
            but padding cannot save you from a child that is simply wider than the panel,
            and these drawers are full of things that are: raw tool output, hostnames,
            model-written claims with a 200-character url in them. Unbroken, they run past
            the right edge and get clipped, which reads as missing padding on that side
            and is not. */}
        <div
          className={`min-w-0 break-words ${fill ? 'flex-1 min-h-0' : 'p-4'}`}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
